import { IQuote, IQuoteLineItem, IQuoteSection, QuoteSectionRequest } from "../../shared/types";
import { toLineItemRequest } from "../QuoteLineItemGrid/quoteLineItemUtils";
import { isBeforeIso, todayIso } from "../../shared/quoteDate";

// Issue 32: a comparison section as the editor holds it in memory. `name` is the raw text
// of the name field - "" means "automatically named". `key` is a stable client id for React.
// Issue 33: `dates` is the section's start / end / co-term choice; "" start means "no dates set".
export type EditorSectionDates = {
  startDate: string;
  endDate: string;
  endDateSource: "" | "manual" | "coterm";
  cotermTargetIds: string | null;
  cotermTargetLabel: string | null;
};
export type EditorSection = { key: string; name: string; dates: EditorSectionDates; items: IQuoteLineItem[] };

export const emptySectionDates = (): EditorSectionDates =>
  ({ startDate: "", endDate: "", endDateSource: "", cotermTargetIds: null, cotermTargetLabel: null });

let sectionKeySeq = 0;
export const newSectionKey = () => `section-${Date.now().toString(36)}-${sectionKeySeq++}`;

export const newEditorSection = (items: IQuoteLineItem[] = []): EditorSection =>
  ({ key: newSectionKey(), name: "", dates: { ...emptySectionDates(), startDate: todayIso() }, items });

// The display name for a section at `index` (0-based): its custom name, or "Option {N}".
export const sectionDisplayName = (name: string | null | undefined, index: number) =>
  (name || "").trim() || `Option ${index + 1}`;

const datesFromSection = (section: IQuoteSection): EditorSectionDates => ({
  startDate: section.start_date || "",
  endDate: section.end_date || "",
  endDateSource: section.end_date_source || "",
  cotermTargetIds: section.coterm_target_subscription_ids ?? null,
  cotermTargetLabel: section.coterm_target_label ?? null,
});

export const editorSectionsFromQuote = (quote: Pick<IQuote, "sections">): EditorSection[] => {
  const sections = quote.sections?.length ? quote.sections : [{ name: null, line_items: [] } as IQuoteSection];
  return sections.map(section => ({
    key: newSectionKey(), name: (section.name || "").trim(),
    dates: datesFromSection(section), items: section.line_items || [],
  }));
};

export const flattenLines = (sections: EditorSection[]) => sections.flatMap(section => section.items);

export const isSectionEmpty = (section: EditorSection) => section.items.length === 0;

// Issue 33: a section only sends dates once staff have set a start date.
export const toSectionRequest = (section: EditorSection): QuoteSectionRequest => {
  const dates = section.dates.startDate
    ? {
      start_date: section.dates.startDate,
      end_date: section.dates.endDate || null,
      end_date_source: section.dates.endDateSource || null,
      coterm_target_subscription_ids: section.dates.endDateSource === "coterm" ? section.dates.cotermTargetIds : null,
      coterm_target_label: section.dates.endDateSource === "coterm" ? section.dates.cotermTargetLabel : null,
    }
    : {};
  return { name: section.name.trim() || null, ...dates, line_items: section.items.map(toLineItemRequest) };
};

// Issue 50 (50.2): a live-preview request now reprices EVERY line, manual included - a manual
// line's Sale/Cost/Total/Margin need the same live, pre-save correctness an automatic line already
// gets. Kept as its own named export (identical to toSectionRequest today) rather than merged, in
// case preview and save diverge again later the way they used to.
export const toPreviewSectionRequest = (section: EditorSection): QuoteSectionRequest => toSectionRequest(section);

const comparableName = (name: string | null | undefined) => (name || "").trim();
const comparableDates = (dates: EditorSectionDates) => JSON.stringify([
  dates.startDate, dates.endDate, dates.endDateSource,
  dates.endDateSource === "coterm" ? dates.cotermTargetIds : null,
]);

const lineSignature = (item: IQuoteLineItem) => {
  const { product_id, sku_id, segment, term_duration, quantity, inherits_section_dates, start_date, end_date, end_date_source } = item;
  // A promotion being attached or detached (auto-attach, Decline/Accept, the Promos picker, a
  // prerequisite added or removed) or its customer share changing moves Cost/Sale/Margin too, so
  // it reprices like any other edit. Without this, a promotion attaching a moment after the line's
  // own reprice (it waits on the background promotion check) left the line showing pre-promotion
  // prices until Save. Cannot loop: the preview response only ever normalizes these fields, and the
  // grid re-attaches a promotion only when a new promotion check comes back, never on a reprice.
  const promo = item.promo_source ? {
    promo_source: item.promo_source, promo_id: item.promo_id ?? null, customer_promo_pct: item.customer_promo_pct ?? 0,
    microsoft_promo_pct: item.promo_source === "manual" ? item.microsoft_promo_pct ?? null : null,
  } : null;
  const base = inherits_section_dates === false
    ? { product_id, sku_id, segment, term_duration, quantity, start_date, end_date, end_date_source, promo }
    : { product_id, sku_id, segment, term_duration, quantity, promo };
  // Issue 50 (50.2): a manual line's own raw inputs now participate too, so editing MSRP/Cost/
  // Discount on a manual line triggers the same debounced live preview an automatic line's
  // product/date change already does - without this, the signature would never change while
  // typing into a manual line's price fields.
  return item.is_price_manual
    ? { ...base, manual_msrp_unit_price: item.manual_msrp_unit_price, manual_cost_unit_price: item.manual_cost_unit_price, requested_discount_pct: item.requested_discount_pct }
    : base;
};

// What a repricing effect (CreateQuotePage / QuoteDetailPage) compares against "what was last
// priced": each section's own dates, every line's product/segment/term/quantity plus its own date
// override (if any), and - since Issue 50 - a manual line's own raw MSRP/Cost/Discount. The priced
// OUTPUT (Sale/Total/Margin) deliberately does not participate, to avoid a response-triggered loop.
export const sectionsPriceSignature = (sections: EditorSection[]) => sections.map(section => ({
  dates: comparableDates(section.dates),
  lines: section.items.map(lineSignature),
}));

// Position-based structural diff between the saved quote and the editor's sections: section
// count, each section's custom name, order, and each section's line list (via `lineKey`).
export const sectionsChanged = (
  saved: IQuoteSection[],
  edited: EditorSection[],
  lineKey: (line: IQuoteLineItem) => unknown,
) => {
  if (saved.length !== edited.length) return true;
  return saved.some((savedSection, index) => {
    const editedSection = edited[index];
    if (comparableName(savedSection.name) !== comparableName(editedSection.name)) return true;
    // Issue 33: a section date / co-term change is a real edit.
    if (comparableDates(datesFromSection(savedSection)) !== comparableDates(editedSection.dates)) return true;
    const savedLines = savedSection.line_items || [];
    if (savedLines.length !== editedSection.items.length) return true;
    return JSON.stringify(savedLines.map(lineKey)) !== JSON.stringify(editedSection.items.map(lineKey));
  });
};

// A section- or line-level start date that's now in the past - only meaningful right after
// restoring a draft that may have sat for a day or more; a plain edit never needs this since the
// date picker itself already enforces `min={todayIso()}` (QuoteDatesModal). `lineIndex` is null for
// a section-level date; otherwise it names a line whose own date overrides the section (Issue 33).
export type StaleStartDate = { sectionIndex: number; lineIndex: number | null };

export const staleStartDates = (sections: EditorSection[], today = todayIso()): StaleStartDate[] => {
  const stale: StaleStartDate[] = [];
  sections.forEach((section, sectionIndex) => {
    if (isBeforeIso(section.dates.startDate, today)) stale.push({ sectionIndex, lineIndex: null });
    section.items.forEach((item, lineIndex) => {
      if (item.inherits_section_dates === false && isBeforeIso(item.start_date || "", today)) stale.push({ sectionIndex, lineIndex });
    });
  });
  return stale;
};

// Bumps every stale start date (section- and line-level) found by staleStartDates to `today`,
// leaving already-future dates untouched. End dates are deliberately left as-is - only start_date
// is corrected; the existing "end_date must be after start_date" validation still applies.
export const bumpStaleStartDates = (sections: EditorSection[], today = todayIso()): EditorSection[] =>
  sections.map(section => ({
    ...section,
    dates: isBeforeIso(section.dates.startDate, today) ? { ...section.dates, startDate: today } : section.dates,
    items: section.items.map(item => item.inherits_section_dates === false && isBeforeIso(item.start_date || "", today)
      ? { ...item, start_date: today } : item),
  }));

// Move the section at `from` to `to`, returning a new array (used by Move Up / Move Down).
export const moveSection = (sections: EditorSection[], from: number, to: number) => {
  if (to < 0 || to >= sections.length || from === to) return sections;
  const next = [...sections];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};
